// Rendering figures that came out of somebody else's accounting software.
//
// ⚠️ EVERY NUMBER HERE IS TALLY'S, NOT OURS. The mirror stores what Tally
// reported and nothing recomputes it (see models/TallyVoucher.js and
// utils/tally/ingest.js on the server). These helpers format; they never
// derive, net, or "tidy up" a figure — the whole value of showing a preparer
// this screen is that it agrees with the client's own display to the rupee.

/**
 * A Tally amount, Indian-grouped.
 *
 * ⚠️ ZERO IS PRINTED, NOT BLANKED. A ledger with a nil closing balance is a
 * real and meaningful state — it says the account settled — and rendering it
 * as an em dash makes it indistinguishable from a field we failed to read.
 * Only null/undefined get the dash.
 */
export function money(n) {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
}

/**
 * Tally's sign convention, preserved.
 *
 * A negative closing balance in Tally is a credit balance; it is stored signed
 * exactly as Tally reported it. Showing it as "Dr/Cr" would be OUR reading of
 * their convention, and the moment that reading is wrong for one account type
 * every figure beneath it is arguable. The sign travels; the label does not.
 */
export const signed = (n) => money(n);

/** Dates on a voucher are dates a person entered, so they are shown as dates. */
export const day = (d) => (d
  ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—');

export const dayTime = (d) => (d
  ? new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
  : '—');

/** `<input type="date">` wants YYYY-MM-DD; a Date renders as an ISO instant. */
export const dateInput = (d) => {
  if (!d) return '';
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return '';
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

/**
 * The Indian financial year containing a date — 1 April to 31 March.
 *
 * Mirrors the server's `utils/tally/sync.js#financialYearWindow`, which is what
 * the report endpoint defaults to. The entries view opens on the same window so
 * the two screens describe the same period without the user setting anything.
 */
export function financialYear(ref = new Date()) {
  const year = ref.getMonth() >= 3 ? ref.getFullYear() : ref.getFullYear() - 1;
  return { from: new Date(year, 3, 1), to: new Date(year + 1, 2, 31) };
}

export const REPORT_LABEL = {
  trial_balance: 'Trial balance',
  receivables: 'Receivables',
  payables: 'Payables',
};

// The three Tally computes for us. Kept in this order because it is the order a
// preparer works in: what do the books say, then who owes us, then whom we owe.
export const REPORT_TYPES = ['trial_balance', 'receivables', 'payables'];

/**
 * Is this report a list of outstanding bills rather than a balance listing?
 *
 * The server decides the same thing in `ingest.ingestReport` in order to fill
 * `rows` or `bills`; this is the display side of that one fact.
 */
export const isBillReport = (t) => t === 'receivables' || t === 'payables';
